import React, { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { StaffApis } from "apis/staff";
import logoImage from "assets/logo/logo_icon_no_bg.png";

const PasswordResetPage = () => {
    const [password, setPassword] = useState("");
    const [rePassword, setRePassword] = useState("");
    const navigate = useNavigate();

    const handleClick = async () => {
        if (!password || !rePassword) {
            toast.error("비밀번호를 입력해주세요.");
            return;
        }
        if (password !== rePassword) {
            toast.error("비밀번호가 일치하지 않습니다.");
            return;
        }
        if (password.length < 4) {
            toast.error("비밀번호는 4자 이상이어야 합니다.");
            return;
        }

        try {
            const ret = await StaffApis.getMyInfo();
            const userId = ret?.token?.data?.user_id;
            await StaffApis.resetPassword(userId, password);
            toast.success("비밀번호가 변경되었습니다.");
            navigate("/");
        } catch (e) {
            console.error("[handleClick] error", e);
            toast.error("비밀번호 변경에 실패했습니다.");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleClick();
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
             style={{
                 background: '#0A1E3D',
                 fontFamily: "'Noto Sans KR', sans-serif"
             }}>

            {/* Floating Background Shapes */}
            <div className="absolute w-64 h-64 top-10 left-10 bg-white bg-opacity-10 rounded-full animate-pulse"
                 style={{animation: 'float 6s ease-in-out infinite'}}></div>
            <div className="absolute w-32 h-32 top-1/3 right-20 bg-white bg-opacity-10 rounded-full animate-pulse"
                 style={{animation: 'float 6s ease-in-out infinite', animationDelay: '2s'}}></div>
            <div className="absolute w-48 h-48 bottom-20 left-1/4 bg-white bg-opacity-10 rounded-full animate-pulse"
                 style={{animation: 'float 6s ease-in-out infinite', animationDelay: '4s'}}></div>

            <div className="w-full max-w-md">
                {/* Logo Section */}
                <div className="flex items-center justify-center mb-8 gap-4">
                    <div className="flex items-center justify-center w-20 h-20 bg-white rounded-2xl shadow-lg p-0.5 flex-shrink-0">
                        <img src={logoImage} alt="유원ENF 로고" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex flex-col">
                        <h1 className="text-3xl font-bold text-white">유원ENF</h1>
                        <p className="text-blue-100 text-sm mt-0.5">인트라넷 시스템</p>
                    </div>
                </div>

                {/* Password Reset Card */}
                <div className="rounded-2xl p-8 shadow-2xl"
                     style={{
                         backdropFilter: 'blur(16px) saturate(180%)',
                         backgroundColor: 'rgba(255, 255, 255, 0.75)',
                         border: '1px solid rgba(209, 213, 219, 0.3)',
                         animation: 'slideUp 0.6s ease-out'
                     }}>

                    {/* Title */}
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-700 rounded-xl mb-4">
                            <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800">비밀번호 재설정</h2>
                        <p className="text-gray-500 text-sm mt-2">새로운 비밀번호를 설정해주세요</p>
                    </div>

                    <form className="space-y-5" onKeyDown={handleKeyDown}>
                        {/* New Password Input */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                                새 비밀번호
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                                    </svg>
                                </div>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-transparent transition-all duration-200"
                                    placeholder="새 비밀번호를 입력하세요"
                                />
                            </div>
                        </div>

                        {/* Confirm Password Input */}
                        <div>
                            <label htmlFor="rePassword" className="block text-sm font-medium text-gray-700 mb-2">
                                비밀번호 확인
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                </div>
                                <input
                                    type="password"
                                    id="rePassword"
                                    name="rePassword"
                                    value={rePassword}
                                    onChange={(e) => setRePassword(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-700 focus:border-transparent transition-all duration-200"
                                    placeholder="비밀번호를 다시 입력하세요"
                                />
                            </div>
                            {/* Password Match Indicator */}
                            {rePassword && (
                                <p className={`mt-2 text-sm ${password === rePassword ? 'text-green-600' : 'text-red-500'}`}>
                                    {password === rePassword ? '✓ 비밀번호가 일치합니다' : '✗ 비밀번호가 일치하지 않습니다'}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="pt-2">
                            <button
                                type="button"
                                onClick={handleClick}
                                disabled={!password || !rePassword || password !== rePassword}
                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-700 hover:transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                style={{
                                    background: '#0A1E3D',
                                }}
                            >
                                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                                    <svg className="h-5 w-5 text-blue-200 group-hover:text-blue-100 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                                    </svg>
                                </span>
                                비밀번호 변경
                            </button>
                        </div>
                    </form>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center">
                    <p className="text-sm text-blue-100">
                        © 2025 유원ENF. All rights reserved.
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                button:not(:disabled):hover {
                    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3) !important;
                }
            `}</style>
        </div>
    );
};

export default PasswordResetPage;
